
import { ReactNode } from "react";
import { ArrowRight } from "lucide-react";
import { Button, ContactButton } from "@/components/ui/button";
import { Container } from "@/components/ui/container";
import { HeroBackground } from "@/components/ui/hero-background";

interface HeroSectionProps {
  title: string;
  highlighted?: string;
  description: string;
  ctaText: string;
  secondaryButtonText?: string;
  secondaryButtonAction?: () => void;
  secondaryButtonLink?: string;
  modalTitle?: string;
  modalDescription?: string;
  modalFormId?: string;
  additionalFields?: Record<string, string>;
  heroPattern?: "roadmap" | "grid" | "dots"; // Removed 'waves' as it's not a valid pattern
  icon?: ReactNode;
}

export const HeroSection = ({
  title,
  highlighted,
  description,
  ctaText,
  secondaryButtonText,
  secondaryButtonAction,
  secondaryButtonLink,
  modalTitle = "Schedule a Consultation",
  modalDescription = "Tell us about your security needs and challenges.",
  modalFormId = "dfa977cc-c726-4029-89d1-d8f757482a67",
  additionalFields = {
    lead_source: "Website",
    lead_interest: "Cybersecurity Services",
  },
  heroPattern = "roadmap",
  icon
}: HeroSectionProps) => {
  return (
    <HeroBackground 
      pattern={heroPattern} 
      colorScheme="purple" 
      height="h-[600px] md:h-[700px]"
      className="animate-fade-in"
    >
      {/* Content container */}
      <Container className="pt-24 pb-12 md:pt-32 md:pb-16">
        <div className="max-w-4xl mx-auto text-center">
          <h1 id="hero-heading" className="text-4xl sm:text-5xl md:text-6xl font-bold mb-6 leading-tight tracking-tight text-gray-900 animate-fade-up">
            {highlighted ? (
              <>
                {title}{" "}
                <span className="bg-gradient-to-r from-edgefi-purple to-edgefi-amethyst bg-clip-text text-transparent">
                  {highlighted}
                </span>
              </>
            ) : title}
          </h1>
          <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto leading-relaxed font-light animate-fade-up" style={{ animationDelay: "0.1s" }}>
            {description}
          </p>
          <div className="flex flex-wrap justify-center gap-4 animate-fade-up" style={{ animationDelay: "0.2s" }}>
            <ContactButton
              className="bg-edgefi-purple hover:bg-edgefi-purple/90 text-white group"
              modalTitle={modalTitle}
              modalDescription={modalDescription}
              modalFormId={modalFormId}
              additionalFields={additionalFields}
              variant="cta"
              size="lg"
            >
              {ctaText}
              <ArrowRight className="ml-2 h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </ContactButton>
            
            {secondaryButtonText && secondaryButtonLink && (
              <a href={secondaryButtonLink}>
                <Button variant="outline" size="lg" className="border-edgefi-purple/20">
                  {secondaryButtonText}
                </Button>
              </a>
            )}
            
            {secondaryButtonText && secondaryButtonAction && !secondaryButtonLink && (
              <Button 
                variant="outline" 
                size="lg" 
                className="border-edgefi-purple/20" 
                onClick={secondaryButtonAction}
              >
                {secondaryButtonText}
              </Button>
            )}
          </div>
        </div>
      </Container>
    </HeroBackground>
  );
};
